const isWhitespaceTextNode = (node: ChildNode) =>
  node.nodeType === Node.TEXT_NODE && (node.textContent ?? '').trim().length === 0;

const isEmptyEditorHtml = (html: string) => {
  const withoutEmptyBlocks = html.replace(/<(p|div)>\s*(<br\s*\/?>)?\s*<\/\1>/gi, '');
  const withoutBreaks = withoutEmptyBlocks.replace(/<br\s*\/?>/gi, '');
  const textOnly = withoutBreaks
    .replace(/&nbsp;/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .trim();

  return textOnly.length === 0 && !/<img\b/i.test(withoutBreaks);
};

const isImageOnlyParagraph = (paragraph: HTMLParagraphElement) => {
  const meaningfulChildren = Array.from(paragraph.childNodes).filter(
    (node) => !isWhitespaceTextNode(node)
  );

  if (meaningfulChildren.length === 0) {
    return false;
  }

  let hasImage = false;

  for (const node of meaningfulChildren) {
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return false;
    }

    const element = node as HTMLElement;
    const tagName = element.tagName.toUpperCase();

    if (tagName === 'BR') {
      continue;
    }

    if (tagName === 'IMG') {
      hasImage = true;
      continue;
    }

    if (tagName === 'A') {
      const linkChildren = Array.from(element.childNodes).filter(
        (child) => !isWhitespaceTextNode(child)
      );

      if (
        linkChildren.length === 1 &&
        linkChildren[0].nodeType === Node.ELEMENT_NODE &&
        (linkChildren[0] as HTMLElement).tagName.toUpperCase() === 'IMG'
      ) {
        hasImage = true;
        continue;
      }
    }

    return false;
  }

  return hasImage;
};

export const normalizeQuillHtml = (html: string) => {
  if (!html) {
    return html;
  }

  if (isEmptyEditorHtml(html)) {
    return '';
  }

  if (typeof DOMParser === 'undefined') {
    return html.replace(/<p>\s*(<img\b[^>]*>)\s*<\/p>/gi, '$1');
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  Array.from(doc.body.querySelectorAll('p')).forEach((paragraph) => {
    if (!isImageOnlyParagraph(paragraph)) {
      return;
    }

    const fragment = doc.createDocumentFragment();
    Array.from(paragraph.childNodes).forEach((node) => {
      if (isWhitespaceTextNode(node)) {
        return;
      }

      if (
        node.nodeType === Node.ELEMENT_NODE &&
        (node as HTMLElement).tagName.toUpperCase() === 'BR'
      ) {
        return;
      }

      fragment.append(node);
    });

    paragraph.replaceWith(fragment);
  });

  return doc.body.innerHTML;
};
